import { createContext, useContext, useMemo } from "react";
import { useJsApiLoader, type Libraries } from "@react-google-maps/api";

type GoogleMapContextType = {
  isLoaded: boolean;
  loadError: Error | undefined;
};

const GoogleMapContext = createContext<GoogleMapContextType | undefined>(undefined);

const GOOGLE_MAP_LIBRARIES: Libraries = ["places"];

export const GoogleMapProvider = ({ children }: any) => {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;

  const loaderConfig = useMemo(
    () => ({
      googleMapsApiKey: apiKey ?? "",
      libraries: GOOGLE_MAP_LIBRARIES,
    }),
    [apiKey]
  );

  const { isLoaded, loadError } = useJsApiLoader(loaderConfig);
  const normalizedError = apiKey ? loadError : new Error("Google Maps API key is not configured.");

  const value = useMemo(
    () => ({
      isLoaded: Boolean(apiKey) && isLoaded,
      loadError: normalizedError,
    }),
    [apiKey, isLoaded, normalizedError]
  );

  return (
    <GoogleMapContext.Provider value={value}>
      {children}
    </GoogleMapContext.Provider>
  );
};

export const useGoogleMap = () => {
  const context = useContext(GoogleMapContext);
  if (!context) {
    throw new Error(
      "useGoogleMap must be used within a GoogleMapProvider"
    );
  }
  return context;
};

